import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit} from "@angular/core";
import {HttpErrorResponse} from "@angular/common/http";
import {SiteProvidersService} from "../../services/site-providers.service";
import {SiteProviderModel} from "../../models/site-provider.model";
import {SiteProviderRequestModel} from "../../models/site-provider-request.model";
import {SiteProviderKind} from "../../types/site-provider-kind.type";
import {SITE_PROVIDER_TABS, SiteProviderTab} from "../../constants/site-provider-tabs.constant";

require('./site-providers-view.scss');
@Component({
  standalone: false,
  selector: 'app-site-providers-view',
  templateUrl: './site-providers-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteProvidersViewComponent implements OnInit {

  public readonly tabs: SiteProviderTab[] = SITE_PROVIDER_TABS;

  public selectedKind: SiteProviderKind = SITE_PROVIDER_TABS[0].kind;
  public providers: SiteProviderModel[] = [];
  public isLoading: boolean;

  // GET returns every data source in one shot (archived included) - fetched once and filtered
  // client-side per tab, so switching tabs never refetches.
  private allProviders: SiteProviderModel[] = [];

  // Guards the very first load, which can resolve while ngOnInit is still on the stack (a fast
  // response, or a caching layer answering synchronously) - detectChanges() would re-enter that
  // change detection pass if called before it finishes.
  private isInitialized = false;

  constructor(@Inject(SiteProvidersService) private siteProvidersService: SiteProvidersService,
              @Inject('siteProvidersDialogsService') private siteProvidersDialogsService,
              @Inject('bringozToastService') private bringozToastService: any,
              @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.loadProviders();
    this.isInitialized = true;
  }

  onSelectTab(kind: SiteProviderKind) {
    if (this.selectedKind === kind) {
      return;
    }

    this.selectedKind = kind;
    this.applyFilter();
  }

  onAddProvider(event: MouseEvent) {
    this.siteProvidersDialogsService.create(event, this.selectedKind)
      .then((payload: SiteProviderRequestModel) => this.save(
        this.siteProvidersService.create(payload), 'SITE_PROVIDER_CREATE_SUCCESS', 'SITE_PROVIDER_CREATE_ERROR'
      ))
      .catch(() => {
        // Dialog dismissed - nothing to create.
      });
  }

  onEditProvider(provider: SiteProviderModel) {
    this.siteProvidersDialogsService.edit(undefined, provider)
      .then((payload: SiteProviderRequestModel) => this.save(
        this.siteProvidersService.update(provider.id, payload), 'SITE_PROVIDER_UPDATE_SUCCESS', 'SITE_PROVIDER_UPDATE_ERROR'
      ))
      .catch(() => {
        // Dialog dismissed - nothing to update.
      });
  }

  onArchiveProvider(provider: SiteProviderModel) {
    this.siteProvidersService.archive(provider.id).subscribe({
      next: () => this.loadProviders(),
      error: (err: HttpErrorResponse) => this.showError('SITE_PROVIDER_ARCHIVE_ERROR', err)
    });
  }

  /**
   * The switch is a controlled input, so the local state is moved first - that is what keeps the
   * rendered switch and the data in agreement, and what puts it back if the call fails.
   */
  onActiveChange({provider, active}: { provider: SiteProviderModel, active: boolean }) {
    this.setActive(provider, active);

    this.siteProvidersService.update(provider.id, this.toRequest(provider, active)).subscribe({
      next: () => this.loadProviders(),
      error: (err: HttpErrorResponse) => {
        this.setActive(provider, !active);
        this.showError('SITE_PROVIDER_UPDATE_ERROR', err);
      }
    });
  }

  // Replaces the row rather than mutating it: the table row is OnPush, so it only re-renders for
  // a new object reference.
  private setActive(provider: SiteProviderModel, active: boolean) {
    this.allProviders = this.allProviders.map(
      (existing) => existing.id === provider.id ? {...existing, active: active} : existing
    );
    this.applyFilter();
    this.cdr.detectChanges();
  }

  // Update replaces the record with whatever the payload holds, so the provider's own fields are
  // resent alongside the new `active` - anything left out is dropped.
  private toRequest(provider: SiteProviderModel, active: boolean): SiteProviderRequestModel {
    return {
      name: provider.name,
      description: provider.description,
      providerKind: provider.providerKind,
      providerType: provider.providerType,
      dataset: provider.dataset,
      table: provider.table,
      endpointId: provider.endpointId,
      active: active
    };
  }

  private save(request$, successKey: string, errorKey: string) {
    request$.subscribe({
      next: () => {
        this.loadProviders();
        this.bringozToastService.success({body: successKey});
      },
      error: (err: HttpErrorResponse) => this.showError(errorKey, err)
    });
  }

  private loadProviders() {
    this.isLoading = true;
    this.siteProvidersService.getAll().subscribe({
      next: (providers) => {
        this.allProviders = providers;
        this.finishLoading();
      },
      // RxJS never calls `complete` after `error` - loading has to be cleared here too, or a
      // failed load leaves the table spinning forever.
      error: (err: HttpErrorResponse) => {
        this.showError('SITE_PROVIDER_LOAD_ERROR', err);
        this.finishLoading();
      }
    });
  }

  private finishLoading() {
    this.isLoading = false;
    this.applyFilter();
    if (this.isInitialized) {
      this.cdr.detectChanges();
    }
  }

  private applyFilter() {
    this.providers = this.allProviders.filter((provider) => provider.providerKind === this.selectedKind);
  }

  // bringoz-toast runs `body`/`details` through `translate` prefixed with 'alerts.', so they have
  // to be real keys under that namespace - `message` is shown as-is, which is where the server's
  // own validation text (more_info, e.g. a duplicate-name rejection) belongs. Matches the
  // convention other *-alerts.service.ts files use for the same HttpErrorResponse shape.
  private showError(bodyKey: string, err: HttpErrorResponse) {
    const moreInfo: string = err?.error?.more_info;
    this.bringozToastService.error({
      body: bodyKey,
      message: moreInfo,
      details: moreInfo ? undefined : 'SOMETHING_WENT_WRONG'
    });
  }

}
